import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Plus, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import ArticleEditor from "./ArticleEditor";
import { AlphaLoader } from "@/components/ui/alpha-loader";

interface Article {
  id: string;
  title: string;
  content: string;
  topic_id: number;
  topic_name: string;
  author: string | null;
  reading_time_minutes: number | null;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

const KinkipediaAdmin = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const { toast } = useToast();

  const fetchArticles = async () => {
    try {
      const { data, error } = await supabase
        .from('kinkipedia_articles')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setArticles(data || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast({
        title: "Error",
        description: "Failed to fetch articles",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const deleteArticle = async (articleId: string) => {
    try {
      const { error } = await supabase
        .from('kinkipedia_articles')
        .delete()
        .eq('id', articleId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Article deleted successfully",
      });
      
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast({
        title: "Error",
        description: "Failed to delete article",
        variant: "destructive",
      });
    }
  };

  const togglePublishStatus = async (article: Article) => {
    try {
      const { error } = await supabase
        .from('kinkipedia_articles')
        .update({ is_published: !article.is_published })
        .eq('id', article.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Article ${!article.is_published ? 'published' : 'unpublished'} successfully`,
      });
      
      fetchArticles();
    } catch (error) {
      console.error('Error updating article:', error);
      toast({
        title: "Error",
        description: "Failed to update article status",
        variant: "destructive",
      });
    }
  };

  const handleArticleSaved = () => {
    setEditingArticle(null);
    setShowCreateDialog(false);
    fetchArticles();
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Kinkipedia Articles</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center h-32">
            <AlphaLoader size="mini" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Kinkipedia Articles</CardTitle>
            <CardDescription>
              Manage educational content and articles ({articles.length} total)
            </CardDescription>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-neon-red hover:bg-neon-red/80 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Create Article
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
              <DialogHeader>
                <DialogTitle>Create New Article</DialogTitle>
                <DialogDescription>
                  Create a new educational article for Kinkipedia
                </DialogDescription>
              </DialogHeader>
              <ArticleEditor onSave={handleArticleSaved} />
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {articles.map((article) => (
            <div key={article.id} className="flex items-center justify-between p-4 border border-ice-white/20 rounded-lg bg-carbon/50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-ice-white truncate">{article.title}</h3>
                  <Badge variant={article.is_published ? "default" : "secondary"}>
                    {article.is_published ? "Published" : "Draft"}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {article.topic_name}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-ice-white/60">
                  <span>By: {article.author || 'Unknown'}</span>
                  <span>{article.reading_time_minutes || 5} min read</span>
                  <span>Updated: {new Date(article.updated_at).toLocaleDateString()}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2 ml-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`/kinkipedia/article/${article.id}`, '_blank')}
                >
                  <Eye className="w-4 h-4" />
                </Button>
                
                <Button
                  variant={article.is_published ? "secondary" : "default"}
                  size="sm"
                  onClick={() => togglePublishStatus(article)}
                >
                  {article.is_published ? "Unpublish" : "Publish"}
                </Button>

                <Dialog open={editingArticle?.id === article.id} onOpenChange={(open) => !open && setEditingArticle(null)}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingArticle(article)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
                    <DialogHeader>
                      <DialogTitle>Edit Article</DialogTitle>
                      <DialogDescription>
                        Modify the article content and settings
                      </DialogDescription>
                    </DialogHeader>
                    {editingArticle && (
                      <ArticleEditor 
                        article={editingArticle} 
                        onSave={handleArticleSaved} 
                      />
                    )}
                  </DialogContent>
                </Dialog>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Article</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete "{article.title}"? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => deleteArticle(article.id)}
                        className="bg-destructive hover:bg-destructive/80"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
          
          {articles.length === 0 && (
            <div className="text-center py-8 text-ice-white/60">
              No articles found. Create your first article!
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default KinkipediaAdmin;